// Public anonymous ticket submission. Anyone can open a ticket with an email;
// on success we show the reference + tokenized follow-up link.
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import type { StylesConfig } from "react-select";
import { brand, colors } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import { useUserStore } from "../../../store/userStore";
import { useUserCompanyName } from "../../../hooks/useUserCompanyName";
import { useIsReseller } from "../../../hooks/useIsReseller";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { useDevicesStore } from "../../../store/admin/devicesStore";
import { authenticateUser } from "../../login/functions";
import { useAlert } from "../../../context/AlertContext";
import {
  getPublicConfig,
  getCompanyUsers,
  getPendingClosure,
  type PublicConfig,
  type CreateTicketResult,
  type TicketPriority,
  type AffectedUser,
} from "../../../Layouts/service-desk/services/serviceDeskService";
import { MODULE_OPTIONS } from "../../../Layouts/service-desk/moduleOptions";
import { INTERNAL_COMPANY_NAME } from "../../../Layouts/service-desk/internalCompany";
import { validateTicketForm, submitSupportTicket } from "./ticketSubmit";
import { isPendingClosureLimitError } from "../../../Layouts/service-desk/services/ticketClosure";

// A dónde va quien tiene 3+ tickets resueltos sin cerrar (#729): la lista de
// seguimiento, con el aviso y los botones para cerrarlos.
const PENDING_CLOSURE_REDIRECT = "/soporte/seguimiento?pendientes=1";

const PRIORITIES: { id: TicketPriority; label: string }[] = [
  { id: "low", label: "Baja" },
  { id: "medium", label: "Media" },
  { id: "high", label: "Alta" },
  { id: "urgent", label: "Urgente" },
];

// Con StylesConfig a secas react-select infiere Option = unknown y onChange
// recibe {} (sin .value): las opciones de esta página son { value, label }.
const getSelectStyles = (isDarkMode: boolean): StylesConfig<{ value: string; label: string }, false> => ({
  control: (provided) => ({
    ...provided,
    backgroundColor: isDarkMode ? colors.dark.card : colors.light.card,
    borderColor: isDarkMode ? colors.dark.border : colors.light.border,
    borderRadius: "0.5rem",
    minHeight: "36px",
    boxShadow: "none",
    fontSize: "0.875rem",
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.primary,
    "&:hover": {
      borderColor: isDarkMode ? colors.dark.border : colors.light.borderSecondary,
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.primary,
    fontSize: "0.875rem",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.secondary : colors.text.light.subtle,
    fontSize: "0.875rem",
  }),
  input: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.primary,
    margin: "0",
    padding: "0",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: isDarkMode ? colors.dark.card : colors.light.card,
    borderRadius: "0.5rem",
    marginTop: "0.25rem",
    border: isDarkMode ? `1px solid ${colors.dark.border}` : `1px solid ${colors.light.border}`,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0.5rem",
    maxHeight: "400px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? isDarkMode
        ? colors.dark.border
        : colors.light.border
      : state.isFocused
        ? isDarkMode
          ? colors.dark.border
          : colors.light.hover
        : "transparent",
    color: isDarkMode ? colors.text.dark.primary : colors.text.light.primary,
    cursor: "pointer",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.375rem",
    fontSize: "0.75rem",
    marginBottom: "0.125rem",
    "&:last-child": {
      marginBottom: 0,
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: isDarkMode ? colors.text.dark.secondary : colors.text.light.muted,
    padding: "0 0.5rem",
    "&:hover": {
      color: isDarkMode ? colors.text.dark.primary : colors.text.light.secondary,
    },
  }),
});

export default function SoporteNuevo() {
  const { t } = useTranslation(["login", "common"]);
  const { isDarkMode } = useTheme();
  const selectStyles = getSelectStyles(isDarkMode);
  const user = useUserStore((state) => state.user);
  const companyName = useUserCompanyName();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { isReseller } = useIsReseller();
  const { allCompanies, fetchAllCompanies } = useCompaniesStore();
  const { adminSectionDevices: devices, fetchAdminSectionDevices, resetAdminSectionDevices } = useDevicesStore();
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    brand: "",
    title: "",
    description: "",
    category: "",
    module: "",
    moduleSection: "",
    priority: "medium" as TicketPriority,
    companyId: "",
    affectedUserId: "",
    deviceImeis: [] as string[],
  });
  const [companyUsers, setCompanyUsers] = useState<AffectedUser[]>([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateTicketResult | null>(null);
  const [showGuestModal, setShowGuestModal] = useState(!user);
  const [guestUsername, setGuestUsername] = useState("");
  const [guestPassword, setGuestPassword] = useState("");
  const [guestModalError, setGuestModalError] = useState<string | null>(null);
  const [authenticating, setAuthenticating] = useState(false);

  useEffect(() => {
    getPublicConfig()
      .then((c) => {
        setConfig(c);
        setForm((f) => ({ ...f, brand: c.brands[0]?.id || "", category: c.categories[0]?.id || "" }));
      })
      .catch(() => setError(t("serviceDesk:publicPortal.configLoadError")));
  }, [t]);

  useEffect(() => {
    if (isReseller) {
      fetchAllCompanies();
    }
  }, [isReseller, fetchAllCompanies]);

  // Con 3+ tickets resueltos sin cerrar el backend rechaza el alta (#729); se
  // consulta al entrar para no dejarle llenar un formulario que no va a poder
  // enviar. Si la consulta falla NO se bloquea nada: el backend es la
  // autoridad y `submit` atiende su 409 igual. Depende de una llave y no de
  // `user` porque el store entrega un objeto nuevo en cada rehidratación.
  const sessionKey = user ? String(user.id || user.email || "session") : "";
  // Mientras se consulta no se pinta el formulario: quien está bloqueado lo
  // llenaba en lo que llegaba la respuesta y perdía lo escrito al redirigir.
  const [checkingPending, setCheckingPending] = useState(Boolean(user));
  useEffect(() => {
    if (!sessionKey) {
      setCheckingPending(false);
      return;
    }
    let cancelled = false;
    setCheckingPending(true);
    getPendingClosure()
      .then((pending) => {
        if (cancelled) return;
        if (pending.blocked) navigate(PENDING_CLOSURE_REDIRECT, { replace: true });
        else setCheckingPending(false);
      })
      .catch(() => {
        if (!cancelled) setCheckingPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionKey, navigate]);

  // Cargar dispositivos cuando cambia la compañía
  useEffect(() => {
    if (form.companyId) {
      resetAdminSectionDevices();
      fetchAdminSectionDevices({ limit: 1000, companyId: form.companyId });
    }
  }, [form.companyId, fetchAdminSectionDevices, resetAdminSectionDevices]);

  // Usuarios de la compañía elegida, para el desplegable de usuario afectado.
  // Al cambiar de compañía se descarta la selección previa y se vacía la lista
  // de inmediato: mientras carga la nueva no deben ofrecerse usuarios de la
  // compañía anterior (el backend los rechazaría con 422).
  useEffect(() => {
    setCompanyUsers([]);
    if (!form.companyId) return;
    let cancelled = false;
    setCompanyUsersLoading(true);
    getCompanyUsers(form.companyId)
      .then((users) => {
        if (!cancelled) setCompanyUsers(Array.isArray(users) ? users : []);
      })
      .catch(() => {
        if (!cancelled) setCompanyUsers([]);
      })
      .finally(() => {
        if (!cancelled) setCompanyUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.companyId]);

  useEffect(() => {
    if (user) {
      setShowGuestModal(false);
      // La compañía del usuario en el store manda sobre localStorage (otras
      // pantallas lo sobrescriben); el backend fija la suya a quien no es admin
      // reseller, así que el desplegable debe cargar esa misma compañía.
      const userCompanyId = user.company_id || localStorage.getItem("userCompanyId") || "";
      if (form.name === "" && form.email === "") {
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        setForm((f) => ({
          ...f,
          name: fullName,
          email: user.email || "",
          companyId: userCompanyId,
        }));
      } else if (form.companyId === "" && userCompanyId) {
        // Si el formulario ya tiene datos pero no compañía, asignar la del usuario
        setForm((f) => ({
          ...f,
          companyId: userCompanyId,
        }));
      }
    }
  }, [user]);

  const handleGuestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuestModalError(null);
    if (!guestUsername || !guestPassword) {
      setGuestModalError(t("serviceDesk:publicPortal.emptyFieldsError"));
      return;
    }
    setAuthenticating(true);
    try {
      await authenticateUser(
        guestUsername,
        guestPassword,
        navigate,
        showAlert,
        t,
        false // No redirigir, permitir crear ticket
      );
      // authenticateUser maneja sus errores internamente y no lanza excepciones:
      // verificar en el store si la sesión realmente se creó.
      if (useUserStore.getState().user) {
        // Si la autenticación fue exitosa, redirigir a /soporte para crear ticket
        navigate("/soporte", { replace: true });
      } else {
        setGuestModalError(t("serviceDesk:publicPortal.invalidCredentialsError"));
      }
    } catch {
      setGuestModalError(t("serviceDesk:publicPortal.invalidCredentialsError"));
    } finally {
      setAuthenticating(false);
    }
  };

  const primary = config?.appearance?.primaryColor || brand.primary;

  const update = (key: keyof typeof form, value: string) => setForm((f) => {
    const updated = { ...f, [key]: value };
    // Reset moduleSection when module changes
    if (key === "module") {
      updated.moduleSection = "";
    }
    // El usuario afectado pertenece a la compañía: si cambia la compañía, la
    // selección anterior deja de ser válida.
    if (key === "companyId") {
      updated.affectedUserId = "";
      updated.deviceImeis = [];
    }
    return updated;
  });

  const affectedUser = companyUsers.find((u) => u.id === form.affectedUserId) || null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Compañía y usuario afectado sólo se exigen con sesión: el alta anónima
    // (portal público) no los pide y debe seguir funcionando.
    const validationError = validateTicketForm(form, { authenticated: Boolean(user) });
    if (validationError === "formRequired") {
      setError(t("serviceDesk:publicPortal.formRequired"));
      return;
    }
    if (validationError === "companyRequired") {
      setError("Selecciona la compañía del ticket. Si es personal interno, usa la compañía " + INTERNAL_COMPANY_NAME + ".");
      return;
    }
    if (validationError === "affectedUserRequired") {
      setError("Selecciona el usuario afectado.");
      return;
    }
    setSubmitting(true);
    try {
      // Build description with devices
      let description = form.description || "";
      if (form.deviceImeis.length > 0) {
        const deviceList = form.deviceImeis.map(imei => {
          const device = devices.find(d => d.identifier === imei);
          return `${device?.name || device?.identifier || device?.vehicleName || imei} (IMEI: ${imei})${device?.vehicle?.plate ? ` - Placa: ${device.vehicle.plate}` : ''}`;
        }).join('\n');
        description = description ? `${description}\n\nDispositivos:\n${deviceList}` : `Dispositivos:\n${deviceList}`;
      }

      const ticketData = {
        ...form,
        files,
        companyId: form.companyId,
        affectedUserId: form.affectedUserId,
        description,
      };

      // Con sesión → createMyTicket; sin sesión → createPublicTicket (el alta
      // anónima no envía compañía ni usuario afectado: el backend los ignora y
      // el agente los asigna al triar).
      setResult(await submitSupportTicket(ticketData, { authenticated: Boolean(user) }));
    } catch (err: unknown) {
      // Llegó al tope entre que abrió el formulario y lo envió (le resolvieron
      // otro ticket mientras escribía): mismo destino que la consulta previa.
      if (isPendingClosureLimitError(err)) {
        navigate(PENDING_CLOSURE_REDIRECT, { replace: true });
        return;
      }
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      setError(typeof msg === "string" ? msg : t("serviceDesk:publicPortal.ticketCreationError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingPending && !result) {
    return <div className={`py-16 text-center ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>Cargando…</div>;
  }

  if (result) {
    return (
      <div className={`rounded-2xl border p-8 text-center shadow-sm ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}`}>
        <div
          className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full text-2xl text-white"
          style={{ backgroundColor: primary }}
        >
          ✓
        </div>
        <h1 className={`mb-2 text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{t("serviceDesk:publicPortal.ticketSuccess")}</h1>
        <p className={`mb-4 text-sm ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>
          {t("serviceDesk:publicPortal.ticketSuccessDescription")}
        </p>
        <div className={`mb-6 inline-block rounded-lg border px-5 py-2 text-2xl font-extrabold tracking-wider ${isDarkMode ? "border-dark-border bg-dark-border text-white" : "border-gray-200 bg-gray-50 text-gray-900"}`}>
          {result.reference}
        </div>
        <div>
          <Link
            to={`/soporte/seguimiento?ref=${encodeURIComponent(result.reference)}&token=${encodeURIComponent(result.token)}`}
            className="inline-block rounded-full px-6 py-2.5 text-sm font-semibold text-white"
            style={{ backgroundColor: primary }}
          >
            {t("serviceDesk:publicPortal.viewTicketStatus")}
          </Link>
        </div>
      </div>
    );
  }

  if (showGuestModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className={`w-full max-w-md rounded-2xl border p-8 shadow-lg ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}`}>
          <h2 className={`mb-2 text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{t("serviceDesk:publicPortal.verifyAccount")}</h2>
          <p className={`mb-6 text-sm ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>
            {t("serviceDesk:publicPortal.verifyAccountDescription")}
          </p>

          {guestModalError && (
            <div className={`mb-4 rounded-lg px-4 py-2 text-sm ${isDarkMode ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-700"}`}>{guestModalError}</div>
          )}

          <form onSubmit={handleGuestSubmit} className="space-y-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>{t("serviceDesk:publicPortal.usernameOrEmail")} *</label>
              <input
                type="text"
                required
                className={isDarkMode ? darkInputCls : lightInputCls}
                value={guestUsername}
                onChange={(e) => setGuestUsername(e.target.value)}
                placeholder={t("serviceDesk:publicPortal.usernameOrEmail")}
                autoFocus
              />
            </div>

            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>{t("serviceDesk:publicPortal.password")} *</label>
              <input
                type="password"
                required
                className={isDarkMode ? darkInputCls : lightInputCls}
                value={guestPassword}
                onChange={(e) => setGuestPassword(e.target.value)}
                placeholder={t("serviceDesk:publicPortal.password")}
              />
            </div>

            <button
              type="submit"
              disabled={authenticating}
              className="w-full rounded-full py-3 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: primary }}
            >
              {authenticating ? t("serviceDesk:publicPortal.verifyingButton") : t("serviceDesk:publicPortal.verifyButton")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border p-6 shadow-sm sm:p-8 ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}`}>
      <h1 className={`mb-1 text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{t("serviceDesk:publicPortal.newTicketTitle")}</h1>
      <p className={`mb-6 text-sm ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>
        {t("serviceDesk:publicPortal.newTicketDescription")}
      </p>

      {config?.appearance?.portalIntro && (
        <div
          className={`mb-6 rounded-lg border-l-4 px-4 py-3 text-sm ${isDarkMode ? "bg-dark-border text-white/70" : "bg-gray-50 text-gray-700"}`}
          style={{ borderColor: primary }}
        >
          {config.appearance.portalIntro}
        </div>
      )}

      {error && <div className={`mb-4 rounded-lg px-4 py-2 text-sm ${isDarkMode ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-700"}`}>{error}</div>}

      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tu nombre">
            <input className={isDarkMode ? darkInputCls : lightInputCls} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Nombre y apellido" />
          </Field>
          <Field label="Correo electrónico *">
            <input type="email" required className={isDarkMode ? darkInputCls : lightInputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="tu@correo.com" />
          </Field>
        </div>

        {user && isReseller && (
          <div>
            <Field label="Compañía *">
              <Select
                styles={selectStyles}
                options={allCompanies.map((c) => ({ value: c.id, label: c.name || c.business_name }))}
                value={form.companyId ? { value: form.companyId, label: allCompanies.find(c => c.id === form.companyId)?.name || allCompanies.find(c => c.id === form.companyId)?.business_name || "" } : null}
                onChange={(opt) => update("companyId", opt?.value || "")}
                placeholder="Seleccionar compañía..."
                isClearable={false}
              />
            </Field>
            <p className={`mt-1 text-xs ${isDarkMode ? "text-white/60" : "text-gray-500"}`}>
              Si el usuario afectado —o quien reporta— es personal interno, selecciona la compañía{" "}
              <strong>{INTERNAL_COMPANY_NAME}</strong>.
            </p>
          </div>
        )}
        {user && !isReseller && companyName && (
          <div className={`rounded-lg border px-3 py-2 ${isDarkMode ? "border-dark-border bg-dark-border" : "border-gray-300 bg-gray-50"}`}>
            <span className={`text-xs font-medium ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>Compañía</span>
            <div className={`mt-1 text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{companyName}</div>
          </div>
        )}

        {form.companyId && (
          <div>
            <Field label="Usuario afectado *">
              <Select
                styles={selectStyles}
                isDisabled={companyUsersLoading}
                options={companyUsers.map((u) => ({
                  value: u.id,
                  label: u.email ? `${u.name} (${u.email})` : u.name,
                }))}
                value={
                  affectedUser
                    ? {
                        value: affectedUser.id,
                        label: affectedUser.email ? `${affectedUser.name} (${affectedUser.email})` : affectedUser.name,
                      }
                    : null
                }
                onChange={(opt) => update("affectedUserId", opt?.value || "")}
                placeholder={companyUsersLoading ? "Cargando usuarios…" : "Seleccionar usuario afectado..."}
                isLoading={companyUsersLoading}
                noOptionsMessage={() => "Esta compañía no tiene usuarios"}
                isClearable={false}
              />
            </Field>
            <p className={`mt-1 text-xs ${isDarkMode ? "text-white/60" : "text-gray-500"}`}>
              Es la persona a la que le ocurre el problema, que no siempre es quien abre el ticket.
            </p>
            {affectedUser && (
              <div className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${isDarkMode ? "bg-dark-border text-white/80" : "bg-gray-100 text-gray-700"}`}>
                <i className="ri-shield-user-line" />
                Rol: <strong>{affectedUser.roleName || "sin rol asignado"}</strong>
              </div>
            )}
          </div>
        )}
        {form.companyId && (
          <div>
            <Field label="Dispositivos">
              <div className="space-y-2">
                <Select
                  styles={selectStyles}
                  options={devices
                    .filter((d) => !form.deviceImeis.includes(d.identifier))
                    .map((d) => ({
                      value: d.identifier,
                      label: `${d.name || d.identifier || d.vehicleName || d.id} ${d.identifier ? `(IMEI: ${d.identifier})` : ''} ${d.vehicle?.plate ? `(Placa: ${d.vehicle.plate})` : ''}`,
                    }))}
                  value={null}
                  onChange={(opt) => {
                    if (opt?.value) {
                      setForm((f) => ({ ...f, deviceImeis: [...f.deviceImeis, opt.value] }));
                    }
                  }}
                  placeholder="Seleccionar dispositivo..."
                  isClearable
                />
                <div className={`rounded-lg border p-2 ${isDarkMode ? "border-dark-border bg-dark-border" : "border-gray-300 bg-gray-50"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <label className={`block text-xs font-medium ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>Dispositivos seleccionados</label>
                    {form.deviceImeis.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setForm((f) => ({ ...f, deviceImeis: [] }));
                        }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Limpiar todos
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {form.deviceImeis.map((imei) => {
                      const device = devices.find((d) => d.identifier === imei);
                      return (
                        <div
                          key={imei}
                          className={`flex items-center justify-between px-2 py-1 rounded ${isDarkMode ? "bg-dark-card text-white" : "bg-white"}`}
                        >
                          <span className="text-sm">
                            {device?.name || device?.identifier || device?.vehicleName || imei}
                            {device?.identifier && <span className={`text-xs ml-2 ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>(IMEI: {device.identifier})</span>}
                            {device?.vehicle?.plate && <span className={`text-xs ml-2 ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>(Placa: {device.vehicle.plate})</span>}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setForm((f) => ({ ...f, deviceImeis: f.deviceImeis.filter((i) => i !== imei) }));
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                    {form.deviceImeis.length === 0 && (
                      <p className={`text-sm ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>Ningún dispositivo seleccionado</p>
                    )}
                  </div>
                </div>
              </div>
            </Field>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Módulo afectado">
            <Select
              styles={selectStyles}
              options={MODULE_OPTIONS.map((m) => ({ value: m.id, label: m.label }))}
              value={form.module ? { value: form.module, label: MODULE_OPTIONS.find(m => m.id === form.module)?.label || "" } : null}
              onChange={(opt) => update("module", opt?.value || "")}
              placeholder="Seleccionar módulo..."
              isClearable
            />
          </Field>
          {form.module && (MODULE_OPTIONS.find(m => m.id === form.module)?.sections?.length ?? 0) > 0 && (
            <Field label="Sección">
              <Select
                styles={selectStyles}
                options={MODULE_OPTIONS.find(m => m.id === form.module)?.sections.map((s) => ({ value: s.id, label: s.label })) || []}
                value={form.moduleSection ? { value: form.moduleSection, label: MODULE_OPTIONS.find(m => m.id === form.module)?.sections.find(s => s.id === form.moduleSection)?.label || "" } : null}
                onChange={(opt) => update("moduleSection", opt?.value || "")}
                placeholder="Seleccionar sección..."
                isClearable
              />
            </Field>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Marca / producto *">
            <Select
              styles={selectStyles}
              options={config?.brands.map((b) => ({ value: b.id, label: b.label })) || []}
              value={form.brand ? { value: form.brand, label: config?.brands.find(b => b.id === form.brand)?.label || "" } : null}
              onChange={(opt) => update("brand", opt?.value || "")}
              placeholder="Seleccionar marca..."
              isClearable={false}
            />
          </Field>
          <Field label="Categoría">
            <Select
              styles={selectStyles}
              options={config?.categories.map((c) => ({ value: c.id, label: c.label })) || []}
              value={form.category ? { value: form.category, label: config?.categories.find(c => c.id === form.category)?.label || "" } : null}
              onChange={(opt) => update("category", opt?.value || "")}
              placeholder="Seleccionar categoría..."
              isClearable={false}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Asunto *">
            <input required className={isDarkMode ? darkInputCls : lightInputCls} value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Resumen breve del problema" />
          </Field>
          <Field label="Prioridad">
            <Select
              styles={selectStyles}
              options={PRIORITIES.map((p) => ({ value: p.id, label: p.label }))}
              value={{ value: form.priority, label: PRIORITIES.find(p => p.id === form.priority)?.label || "Media" }}
              onChange={(opt) => update("priority", opt?.value as TicketPriority)}
              placeholder="Seleccionar prioridad..."
              isClearable={false}
            />
          </Field>
        </div>

        <Field label="Descripción">
          <textarea className={isDarkMode ? darkInputCls : lightInputCls} rows={5} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Describe el problema con el mayor detalle posible" />
        </Field>

        <Field label="Evidencias / archivos">
          <FileDropZone files={files} onFilesChange={setFiles} isDarkMode={isDarkMode} />
        </Field>

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full py-3 text-sm font-semibold text-white disabled:opacity-60"
          style={{ backgroundColor: primary }}
        >
          {submitting ? "Enviando…" : "Enviar ticket"}
        </button>
      </form>
    </div>
  );
}

const lightInputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200";
const darkInputCls = "w-full rounded-lg border border-dark-border bg-dark-card px-3 py-2 text-sm text-white focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200";

function FileDropZone({ files, onFilesChange, isDarkMode }: { files: File[]; onFilesChange: (files: File[]) => void; isDarkMode: boolean }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const newFiles = Array.from(e.dataTransfer.files);
    onFilesChange([...files, ...newFiles]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    onFilesChange([...files, ...newFiles]);
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-3">
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors cursor-pointer ${
          dragActive
            ? isDarkMode
              ? 'border-blue-500 bg-blue-950/30'
              : 'border-blue-400 bg-blue-50'
            : isDarkMode
              ? 'border-gray-600 bg-dark-card hover:border-gray-500'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }`}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleChange}
          className="hidden"
          accept="image/*,video/*,.pdf,.txt,.csv,.zip,.json,.doc,.docx"
        />
        <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <i className="ri-upload-cloud-2-line text-2xl mb-2 block"></i>
          Arrastra archivos aquí o haz clic para seleccionar
        </div>
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          Soporta imágenes, videos, PDFs y más (máx. 100MB por archivo)
        </p>
      </div>

      {files.length > 0 && (
        <div className={`rounded-lg border p-4 ${isDarkMode ? 'border-dark-border bg-dark-border/50' : 'border-gray-200 bg-gray-50'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Archivos seleccionados ({files.length})
          </h3>
          <div className="space-y-2">
            {files.map((file, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-dark-card' : 'bg-white'}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <i className={`ri-file-line text-lg ${
                    file.type.startsWith('image/') ? 'ri-image-line text-blue-500' :
                    file.type.startsWith('video/') ? 'ri-video-line text-purple-500' :
                    file.type === 'application/pdf' ? 'ri-file-pdf-line text-red-500' :
                    'ri-attachment-2 text-gray-500'
                  }`}></i>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {file.name}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className={`ml-2 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400`}
                  title="Eliminar archivo"
                >
                  <i className="ri-delete-bin-6-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  const { isDarkMode: fieldIsDarkMode } = useTheme();
  return (
    <label className="block">
      <span className={`mb-1 block text-xs font-medium ${fieldIsDarkMode ? "text-white/70" : "text-gray-600"}`}>{label}</span>
      {children}
    </label>
  );
}
